import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    // if already authenticated, redirect
    const t = localStorage.getItem("who_admin_token");
    if (t) {
      axios
        .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${t}` } })
        .then(() => nav("/admin", { replace: true }))
        .catch(() => localStorage.removeItem("who_admin_token"));
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("who_admin_token", data.token);
      toast.success("Welcome back.");
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-deep px-6" data-testid="admin-login-page">
      <div className="absolute inset-0 dot-grid opacity-[0.08] pointer-events-none" aria-hidden />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/15 border border-gold/50 mb-4">
            <Lock className="w-6 h-6 text-gold" />
          </div>
          <p className="eyebrow text-gold-light">WHO · Admin</p>
          <h1 className="mt-3 font-display font-black text-cream text-3xl leading-tight">Sign in to the dashboard</h1>
          <p className="mt-3 text-cream/60 text-sm">Contact submissions · WHO Guides interactions</p>
        </div>

        <form onSubmit={submit} className="bg-white/[0.03] border border-white/10 rounded-lg p-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-cream/70">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              data-testid="admin-login-email"
              className="mt-2 w-full bg-navy-900/60 border border-white/10 focus:border-gold rounded-md px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:ring-1 focus:ring-gold/60"
              placeholder="you@warmhandoffhq.com"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-cream/70">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="admin-login-password"
              className="mt-2 w-full bg-navy-900/60 border border-white/10 focus:border-gold rounded-md px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:ring-1 focus:ring-gold/60"
              placeholder="••••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-5 py-3 rounded-md disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>)}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-cream/40">
          Only WHO staff. If you shouldn&apos;t be here, close this tab.
        </p>
      </div>
    </div>
  );
}
